import { Spinner } from 'components/Elements';
import { Head } from 'components/Head';
import { ContentLayout } from 'components/Layout';
import Breadcrumbs from 'components/Layout/Breadcrumbs';
import { formatDate } from 'utils/format';
import { useParams } from 'react-router-dom';

import { useProduct } from '../api/getProduct';
import { UpdateProduct } from '../components/UpdateProduct';

export const Product = () => {
  const { id } = useParams();
  const productQuery = useProduct({ id });

  const Entry = ({ label, value }) => (
    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 px-6">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{value}</dd>
    </div>
  );

  const routes = [
    {
      name: 'products',
      path: '/app/products',
    },
    {
      name: productQuery.data?.product_title,
      path: `/app/products/${id}`,
    },
  ];

  if (productQuery.isLoading) {
    return (
      <div className="w-full h-48 flex justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!productQuery.data) return null;

  const acceptableCollaterals = productQuery.data?.acceptable_collaterals[0].split(',');

  return (
    <>
      <Head title={productQuery.data.product_title} />
      <ContentLayout title={productQuery.data.product_title} end={<UpdateProduct id={id} />}>
        <Breadcrumbs routes={routes} />
        <div className="border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5 mt-4">
            <Entry
              label="Product Status"
              value={`${productQuery.data?.status}`}
            />
            <Entry
              label="Average Lending Rate"
              value={`${productQuery.data?.average_lending_rate?.toLocaleString()} UGX`}
            />
            <Entry
              label="Salary Collateral "
              value={`${productQuery.data?.salary_collateral_percentage?.toLocaleString()} %`}
            />
            <Entry
              label="Acceptable Collaterals "
              value={
                <div className="">
                  {acceptableCollaterals.map((collateral, index) => (
                    <div key={index} className="text-xs text-gray-900">
                      {collateral}
                    </div>
                  ))}
                </div>
              }
            />
          </dl>
          <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
            <div className="font-bold my-4 px-6">Fees</div>
            <Entry
              label="Service Fee"
              value={`${productQuery.data?.service_fee?.toLocaleString()} UGX`}
            />
            <Entry
              label="Late Fee"
              value={`${productQuery.data?.late_fee?.toLocaleString()} UGX`}
            />
            <Entry
              label="Early Repayment Fee"
              value={`${productQuery.data?.early_repayment_fee?.toLocaleString()} UGX`}
            />
          </dl>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Principal</div>
              <Entry
                label="Minimum"
                value={`${productQuery.data?.principal?.min?.toLocaleString()} UGX`}
              />
              <Entry
                label="Maximum"
                value={`${productQuery.data?.principal?.max?.toLocaleString()} UGX`}
              />
            </dl>
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Interest Rate</div>
              <Entry
                label="Minimum"
                value={`${productQuery.data?.interest_rate?.min?.toLocaleString()} %`}
              />
              <Entry
                label="Maximum"
                value={`${productQuery.data?.interest_rate?.max?.toLocaleString()} %`}
              />
              <Entry
                label="Fixed"
                value={`${productQuery.data?.fixed_interest?.toLocaleString()} %`}
              />
            </dl>
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Period</div>
              <Entry
                label="Minimum"
                value={`${productQuery.data?.period?.min?.toLocaleString()} months`}
              />
              <Entry
                label="Maximum"
                value={`${productQuery.data?.period?.max?.toLocaleString()} months`}
              />
            </dl>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Real Estate</div>
              <Entry
                label="Due Deligence Fee"
                value={`${productQuery.data?.applicable_fees?.real_estate?.due_deligence_fee?.toLocaleString()} UGX`}
              />
              <Entry
                label="Stamp Duty Percentage"
                value={`${productQuery.data?.applicable_fees?.real_estate?.mortgage_placement?.stamp_duty_fees?.toLocaleString()} UGX`}
              />
              <Entry
                label="Registration Fee"
                value={`${productQuery.data?.applicable_fees?.real_estate?.mortgage_placement?.registration_fees?.toLocaleString()} UGX`}
              />
              <Entry
                label="Bank Charges"
                value={`${productQuery.data?.applicable_fees?.real_estate?.mortgage_placement?.bank_charges?.toLocaleString()} UGX`}
              />
              <Entry
                label="Registrar"
                value={`${productQuery.data?.applicable_fees?.real_estate?.mortgage_placement?.registrar_fees?.toLocaleString()} UGX`}
              />
              <Entry
                label="Legal Fees"
                value={`${productQuery.data?.applicable_fees?.real_estate?.mortgage_placement?.legal_fees?.toLocaleString()} UGX`}
              />
            </dl>
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Valuation for Vehicles</div>
              <Entry
                label="Caveat Placement Fee"
                value={
                  productQuery.data?.applicable_fees?.vehicles?.caveat_placement_fee?.toLocaleString() +
                  ' UGX'
                }
              />
              <Entry
                label="Insurance Fee"
                value={
                  productQuery.data?.applicable_fees?.vehicles?.insurance_fee?.toLocaleString() +
                  ' UGX'
                }
              />
              <Entry
                label="Tracker Fee (Hire)"
                value={
                  productQuery.data?.applicable_fees?.vehicles?.tracker_fee?.hire?.toLocaleString() +
                  ' UGX'
                }
              />
              <Entry
                label="Tracker Fee (Buy)"
                value={
                  productQuery.data?.applicable_fees?.vehicles?.tracker_fee?.buy?.toLocaleString() +
                  ' UGX'
                }
              />

              <Entry
                label="Commercial Vehicles"
                value={`${productQuery.data?.applicable_fees?.vehicles?.valuation?.commercial_vehicles?.toLocaleString()} UGX`}
              />
              <Entry
                label="Heavy Vehicles"
                value={`${productQuery.data?.applicable_fees?.vehicles?.valuation?.heavy_vehicles?.toLocaleString()} UGX`}
              />
              <Entry
                label="Personal Vehicles or Bikes"
                value={`${productQuery.data?.applicable_fees?.vehicles?.valuation?.personal_vehicles_or_bikes?.toLocaleString()} UGX`}
              />
            </dl>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Computers</div>
              <Entry
                label="Assessment Fees"
                value={
                  productQuery.data?.applicable_fees?.computers?.assessment_fees?.toLocaleString() +
                  ' UGX'
                }
              />
            </dl>
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Television</div>
              <Entry
                label="Assessment Fees"
                value={
                  productQuery.data?.applicable_fees?.television?.assessment_fees?.toLocaleString() +
                  ' UGX'
                }
              />
            </dl>
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Smart Phones</div>
              <Entry
                label="Assessment Fees"
                value={
                  productQuery.data?.applicable_fees?.smart_phone?.assessment_fees?.toLocaleString() +
                  ' UGX'
                }
              />
            </dl>
            <dl className="sm:divide-y sm:divide-gray-200 border border-gray-200 rounded-2xl shadow-lg mb-5">
              <div className="font-bold my-4 px-6">Other Electronics</div>
              <Entry
                label="Assessment Fees"
                value={
                  productQuery.data?.applicable_fees?.other_electronics?.assessment_fees?.toLocaleString() +
                  ' UGX'
                }
              />
            </dl>
          </div>
        </div>
      </ContentLayout>
    </>
  );
};
